import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function camelToSnakeCase(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function convertCamelToSnakeCase(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertCamelToSnakeCase);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = camelToSnakeCase(key);
    result[snakeKey] = convertCamelToSnakeCase(value);
  }
  return result;
}

export function snakeToCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function convertSnakeToCamelCase(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeToCamelCase);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = snakeToCamelCase(key);
    result[camelKey] = convertSnakeToCamelCase(value);
  }
  return result;
}

export function capitaliseWords(words) {
  if (!words) return "";
  return words
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getItem(variable) {
  try {
    return localStorage.getItem(variable);
  } catch (error) {
    console.error("Error getting item from localStorage:", error);
    return null;
  }
}

export function setItem(variable, value) {
  try {
    localStorage.setItem(variable, value);
  } catch (error) {
    console.error("Error setting item in localStorage:", error);
  }
}

export function removeItem(variable) {
  try {
    localStorage.removeItem(variable);
  } catch (error) {
    console.error("Error removing item from localStorage:", error);
  }
}

export function logout() {
  removeItem("sessionid");
  window.location.href = "/login";
}

export function getAuthErrorMessage(error) {
  const code = error.code;
  console.log("getAuthErrorMessage ", code);
  switch (code) {
    case "auth/user-not-found":
    case "auth/invalid-login-credentials":
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed.";
    case "auth/operation-not-allowed":
      return "Operation not allowed. Please contact support.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    default:
      return error.message || "An unexpected error occurred. Please try again.";
  }
}

// Common websites to exclude from domain input
const COMMON_WEBSITES = [
  // Search engines
  "google.com",
  "bing.com",
  "yahoo.com",
  "duckduckgo.com",
  "baidu.com",

  // Social media platforms
  "facebook.com",
  "twitter.com",
  "x.com",
  "instagram.com",
  "linkedin.com",
  "tiktok.com",
  "youtube.com",
  "reddit.com",
  "pinterest.com",
  "snapchat.com",

  // E-commerce platforms
  "amazon.com",
  "ebay.com",
  "shopify.com",
  "etsy.com",
  "alibaba.com",

  // News and media
  "cnn.com",
  "bbc.com",
  "nytimes.com",
  "washingtonpost.com",
  "reuters.com",
  "foxnews.com",
  "msnbc.com",
  "espn.com",
  "huffpost.com",
  "buzzfeed.com",

  // Technology companies
  "microsoft.com",
  "apple.com",
  "adobe.com",
  "salesforce.com",
  "oracle.com",
  "ibm.com",
  "intel.com",
  "nvidia.com",
  "amd.com",
  "cisco.com",

  // Cloud and hosting services
  "aws.amazon.com",
  "cloud.google.com",
  "azure.microsoft.com",
  "heroku.com",
  "netlify.com",
  "vercel.com",
  "github.com",
  "gitlab.com",
  "bitbucket.org",

  // Communication platforms
  "zoom.us",
  "teams.microsoft.com",
  "slack.com",
  "discord.com",
  "telegram.org",
  "whatsapp.com",
  "skype.com",
  "webex.com",

  // Entertainment
  "netflix.com",
  "hulu.com",
  "disney.com",
  "spotify.com",
  "soundcloud.com",
  "twitch.tv",
  "vimeo.com",
  "dailymotion.com",

  // Other common services
  "wikipedia.org",
  "stackoverflow.com",
  "medium.com",
  "quora.com",
  "craigslist.org",
  "booking.com",
  "airbnb.com",
  "uber.com",
  "lyft.com",
  "paypal.com",
  "stripe.com",
  "square.com",
  "dropbox.com",
  "onedrive.live.com",
  "icloud.com",

  // Development and tools
  "npmjs.com",
  "nodejs.org",
  "reactjs.org",
  "vuejs.org",
  "angular.io",
  "jquery.com",
  "bootstrap.com",
  "tailwindcss.com",
  "webpack.js.org",

  // Government and education
  "gov",
  "edu",
  "mil",
  "org",

  // Local development
  "localhost",
  "127.0.0.1",
  "0.0.0.0",

  // Current application
  "fluvo.ai",
];

// URL validation function
export function isValidUrl(url) {
  try {
    // Strip trailing slash before validation
    const trimmedUrl = url.replace(/\/+$/, "");

    // Normalize URL for validation - add https:// if no protocol is provided
    const urlToTest =
      trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")
        ? trimmedUrl
        : `https://${trimmedUrl}`;

    // Regex pattern for URL validation - supports both http and https
    const urlRegex =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&=]*)$/;

    if (!urlRegex.test(urlToTest)) {
      return false;
    }

    // Extract hostname using regex
    const hostnameMatch = urlToTest.match(/^https?:\/\/(?:www\.)?([^\/\s:]+)/);
    const hostname = hostnameMatch ? hostnameMatch[1] : null;

    console.log({ hostname, urlToTest });

    if (!hostname) {
      return false;
    }

    // Check against common websites list
    const isCommonWebsite = COMMON_WEBSITES.some((commonSite) => {
      // Check for exact match or if hostname ends with the common site
      return (
        hostname === commonSite ||
        hostname.endsWith(`.${commonSite}`)
      );
    });

    if (isCommonWebsite) {
      return false;
    }

    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
}
